import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
import StockItemSelectionModal from '../../../components/StockItemSelectionModal';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaFileExport, 
  FaSearch,
  FaDownload,
  FaArrowRight,
  FaArrowLeft,
  FaBox,
  FaWarehouse,
  FaTruck,
  FaCube,
  FaLayerGroup,
  FaExclamationTriangle,
  FaExpand,
  FaCompress,
  FaBook,
  FaTimes,
  FaTrashAlt
} from 'react-icons/fa';

export default function StockItemsLedger() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [stockItemsList, setStockItemsList] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    location: '',
    stockItemId: null
  });

  // Selected stock item object from modal
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [showStockItemModal, setShowStockItemModal] = useState(false);

  // Fullscreen
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage changes
  useEffect(() => {
    const syncDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(syncDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Handle stock item selection from modal
  const handleStockItemSelect = (item) => {
    setSelectedStockItem(item);
    setFilters(prev => ({
      ...prev,
      stockItemId: { value: item._id, label: item.name }
    }));
    setShowStockItemModal(false);
  };

  const clearStockItemSelection = () => {
    setSelectedStockItem(null);
    setFilters(prev => ({ ...prev, stockItemId: null }));
  };

  // Get company ID from user context
  const getCompanyId = () => {
    if (user?.user?.company) {
      return user.user.company._id;
    }
    return '';
  };

  useEffect(() => {
    fetchLocations();
    fetchStockItemsList();
    // Set default date range as current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date();
    
    setFilters(prev => ({
      ...prev,
      dateFrom: firstDay.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0]
    }));
  }, []);

  const fetchLocations = async () => {
    try {
      const companyId = getCompanyId();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchStockItemsList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/stock-items/stock-items-list?companyId=${getCompanyId()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStockItemsList(data.stockItems || []);
      }
    } catch (error) {
      console.error('Error fetching stock items:', error);
    }
  };

  const fetchLedgerData = async () => {
    if (!filters.stockItemId || !filters.dateFrom || !filters.dateTo) {
      Swal.fire('Warning', 'Please select a stock item and date range', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        stockItemId: filters.stockItemId.value,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        companyId: getCompanyId()
      });

      // Add location only if selected
      if (filters.location) {
        queryParams.append('location', filters.location);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/stock-items/ledger?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLedgerData(data);
      } else {
        Swal.fire('Error', 'Failed to fetch ledger data', 'error');
      }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      Swal.fire('Error', 'Error occurred while fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = async () => {
    if (!filters.stockItemId || !filters.dateFrom || !filters.dateTo) {
      Swal.fire('Warning', 'Please select a stock item and date range', 'warning');
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        stockItemId: filters.stockItemId.value,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        companyId: getCompanyId(),
        export: 'true'
      });

      // Add location only if selected
      if (filters.location) {
        queryParams.append('location', filters.location);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/stock-items/ledger/export?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `stock-items-ledger-${filters.stockItemId.label}-${filters.dateFrom}-to-${filters.dateTo}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        Swal.fire('Success', 'Ledger exported successfully', 'success');
      } else {
        Swal.fire('Error', 'Failed to export ledger', 'error');
      }
    } catch (error) {
      console.error('Error exporting ledger:', error);
      Swal.fire('Error', 'Error occurred while exporting', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-IN');
  };

  const calculateRunningBalance = (transactions, index) => {
    let balance = parseFloat((ledgerData?.openingBalance || 0).toFixed(4));
    for (let i = 0; i <= index; i++) {
      const transaction = transactions[i];
      if (transaction.type === 'INWARD' || transaction.type === 'TRANSFER IN') {
        balance = parseFloat((balance + transaction.quantity).toFixed(4));
      } else if (transaction.type === 'OUTWARD' || transaction.type === 'TRANSFER OUT' || transaction.type === 'DESTROYED') {
        balance = parseFloat((balance - transaction.quantity).toFixed(4));
      }
      // ORDER and DISPATCH are informational - no balance effect
    }
    return balance;
  };

  return (
    <div ref={containerRef} className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'} ${isFullscreen ? 'overflow-auto' : ''}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-8">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <FaBook className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">Stock Items Ledger</h1>
                <p className="text-white/90">Track stock item movement history</p>
              </div>
              <button onClick={toggleFullscreen} className="p-3 rounded-xl transition-colors hover:bg-white/20 text-white/80 hover:text-white" title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                {isFullscreen ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className={`rounded-b-2xl shadow-xl border-2 border-t-0 p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCube className="inline mr-2" />
                  Stock Item *
                </label>
                {selectedStockItem ? (
                  <div className={`flex items-center justify-between px-3 py-2 border-2 rounded-xl transition-all ${darkMode ? 'bg-gray-700 border-indigo-500/50' : 'bg-indigo-50 border-indigo-300'}`}>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedStockItem.name}</div>
                      {selectedStockItem.id && <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedStockItem.id}</div>}
                    </div>
                    <button onClick={clearStockItemSelection} className={`ml-2 p-1 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}>
                      <FaTimes className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowStockItemModal(true)}
                    className={`w-full px-3 py-2.5 border-2 rounded-xl text-left text-sm transition-all hover:border-indigo-400 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600' : 'border-gray-200 text-gray-500 hover:bg-indigo-50'}`}
                  >
                    Click to select stock item...
                  </button>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2" />
                  Date From *
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className={`w-full px-3 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2" />
                  Date To *
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className={`w-full px-3 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaMapMarkerAlt className="inline mr-2" />
                  Location
                </label>
                <select
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className={`w-full px-3 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>{loc.name || loc.locationName}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
          <button
                  onClick={fetchLedgerData}
                  disabled={loading}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaSearch />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Export Options */}
            {ledgerData && (
              <div className={`mt-4 pt-4 border-t-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Export Options:</span>
                  <button
                    onClick={handleExport}
                    disabled={loading}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-1.5 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all disabled:bg-gray-400 flex items-center gap-2 text-sm shadow-md hover:shadow-lg font-medium"
                  >
                    <FaFileExport />
                    Excel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        {ledgerData && ledgerData.transactions && ledgerData.transactions.length > 0 ? (
          <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-6 py-5 border-b-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Ledger Report - {filters.stockItemId?.label} ({formatDate(filters.dateFrom)} to {formatDate(filters.dateTo)})
                </h2>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Total Transactions: {ledgerData.transactions.length}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-b-2xl">
              <table className={`min-w-full border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>
                      Date
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>
                      Location
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>
                      Transaction Type
                    </th>
                    <th className={`px-3 py-3 text-center text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'bg-green-900/30 text-gray-300 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`} colSpan="3">
                      <div className="flex items-center justify-center gap-1">
                        <FaArrowRight className={`${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                        Inward
                      </div>
                    </th>
                    <th className={`px-3 py-3 text-center text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'bg-red-900/30 text-gray-300 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`} colSpan="3">
                      <div className="flex items-center justify-center gap-1">
                        <FaArrowLeft className={`${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                        Outward
                      </div>
                    </th>
                    <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>
                      Balance
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>
                      Reference
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>
                      DO Number
                    </th>
                  </tr>
                  <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <th className={`px-4 py-1 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`} colSpan="3"></th>
                    <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>Fresh</th>
                    <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>Damaged</th>
                    <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>Total</th>
                    <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>Fresh</th>
                    <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>Damaged</th>
                    <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>Total</th>
                    <th className={`px-4 py-1 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`} colSpan="3"></th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                  {/* Opening Balance Row */}
                  <tr className={`font-medium ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                      {formatDate(filters.dateFrom)}
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      -
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-800'}`}>
                        Opening Balance
                      </span>
                    </td>
                    <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'text-blue-400 bg-green-900/30' : 'text-blue-700 bg-green-50'}`} colSpan="3">
                      -
                    </td>
                    <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'text-blue-400 bg-red-900/30' : 'text-blue-700 bg-red-50'}`} colSpan="3">
                      -
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm text-center font-bold ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                      {ledgerData.openingBalance || 0}
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      Opening Stock
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      -
                    </td>
                  </tr>

                  {/* Transaction Rows */}
                  {ledgerData.transactions.map((transaction, index) => (
                    <tr key={index} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatDate(transaction.date)}
                      </td>

                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          <span>{transaction.locationName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.type === 'INWARD' 
                            ? darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-800' 
                            : transaction.type === 'TRANSFER IN' 
                              ? darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-800' 
                              : transaction.type === 'DESTROYED'
                                ? darkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-800'
                                : darkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-800'
                        }`}>
                          {(transaction.type === 'INWARD' || transaction.type === 'TRANSFER IN') ? 
                            <FaArrowRight className="mr-1" /> : 
                            transaction.type === 'DESTROYED' ?
                            <FaTrashAlt className="mr-1" /> :
                            <FaArrowLeft className="mr-1" />
                          }
                          {transaction.type}
                        </span>
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                        {(transaction.type === 'INWARD' || transaction.type === 'TRANSFER IN') ? (
                          <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                            +{transaction.freshQuantity || 0}
                          </div>
                        ) : (
                          <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                        )}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                        {(transaction.type === 'INWARD' || transaction.type === 'TRANSFER IN') ? (
                          <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                            {transaction.damagedQuantity ? `+${transaction.damagedQuantity}` : '-'}
                          </div>
                        ) : (
                          <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                        )}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                        {(transaction.type === 'INWARD' || transaction.type === 'TRANSFER IN') ? (
                          <div className={`font-medium text-lg ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                            +{transaction.quantity}
                          </div>
                        ) : (
                          <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                        )}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                        {(transaction.type === 'OUTWARD' || transaction.type === 'TRANSFER OUT' || transaction.type === 'DESTROYED') ? (
                          <div className={`font-medium ${transaction.type === 'DESTROYED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>
                            -{transaction.freshQuantity || 0}
                          </div>
                        ) : (
                          <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                        )}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                        {(transaction.type === 'OUTWARD' || transaction.type === 'TRANSFER OUT' || transaction.type === 'DESTROYED') ? (
                          <div className={`font-medium ${transaction.type === 'DESTROYED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>
                            {transaction.damagedQuantity ? `-${transaction.damagedQuantity}` : '-'}
                          </div>
                        ) : (
                          <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                        )}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                        {(transaction.type === 'OUTWARD' || transaction.type === 'TRANSFER OUT' || transaction.type === 'DESTROYED') ? (
                          <div className={`font-medium text-lg ${transaction.type === 'DESTROYED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>
                            -{transaction.quantity}
                          </div>
                        ) : (
                          <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                        )}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm text-center font-medium text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {calculateRunningBalance(ledgerData.transactions, index)}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {transaction.reference}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {transaction.deliveryOrderNumber || '-'}
                      </td>
                    </tr>
                  ))}

                  {/* Closing Balance Row */}
                  <tr className={`font-medium ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-purple-400' : 'text-purple-800'}`}>
                      {formatDate(filters.dateTo)}
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                      -
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-800'}`}>
                        Closing Balance
                      </span>
                    </td>
                    <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'text-purple-400 bg-green-900/30' : 'text-purple-700 bg-green-50'}`} colSpan="3">
                      {ledgerData.totalInward || 0}
                    </td>
                    <td className={`px-3 py-4 whitespace-nowrap text-sm text-center ${darkMode ? 'text-purple-400 bg-red-900/30' : 'text-purple-700 bg-red-50'}`} colSpan="3">
                      {ledgerData.totalOutward || 0}
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm text-center font-bold text-lg ${darkMode ? 'text-purple-400' : 'text-purple-800'}`}>
                      {ledgerData.closingBalance || 0}
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                      Closing Stock
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                      -
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Order / Transit Summary */}
            {ledgerData?.orderSummary && (
              <div className={`mt-4 rounded-xl border p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Order / Transit Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-purple-900/20 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>Purchase Orders</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Ordered (Period)</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.dateRange?.ordered || 0}</span></div>
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Pending Orders</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.current?.pendingOrders || 0}</span></div>
                    </div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>In Transit</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Dispatched (Period)</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.dateRange?.dispatched || 0}</span></div>
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Currently In Transit</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.current?.inTransit || 0}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-6 py-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {loading ? (
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div>
                  <FaLayerGroup className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No ledger data found</h3>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Select a stock item and date range to view ledger data
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stock Item Selection Modal */}
      <StockItemSelectionModal
        isOpen={showStockItemModal}
        onClose={() => setShowStockItemModal(false)}
        onSelect={handleStockItemSelect}
        darkMode={darkMode}
        userId={user?.user?._id}
      />
    </div>
  );
}
